import React, { useRef } from 'react';

import { IMessageAttachment } from '@entities/message/model/types';
import { downloadFile, isImg } from '@shared/helpers';
import { useIsOverflowing } from '@shared/hooks';
import { Flex, Icon, Image, UiButton, UiText } from '@shared/ui';

import cls from './MessageAttachments.module.css';

interface IMessageAttachmentProps {
    attachment: IMessageAttachment;
    attachments: IMessageAttachment[];
    onOpenAttachments?: (
        attachments: IMessageAttachment[],
        currentAttachmentId: IMessageAttachment['id']
    ) => void;
}

export const MessageAttachment = ({
    attachment,
    attachments,
    onOpenAttachments,
}: IMessageAttachmentProps) => {
    const elNameRef = useRef(null);
    const { isOverflowing } = useIsOverflowing({ elRef: elNameRef });
    const isImage = isImg(attachment?.mimeType);

    const downloadAttachment = async (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        event.stopPropagation();

        const downloadUrl =
            attachment?.originalUrl || attachment?.previewUrl || attachment?.previewSvgUrl;

        if (!downloadUrl) return;

        await downloadFile(downloadUrl, attachment.name);
    };

    const openAttachments = (event: React.MouseEvent<HTMLDivElement>) => {
        event.stopPropagation();
        onOpenAttachments?.(attachments, attachment.id);
    };

    return (
        <Flex gap={8} fullWidth className={cls.MessageAttachmentRow} onClick={openAttachments}>
            <UiButton
                className={cls.UploadAttachmentBtn}
                variant="icon"
                startIcon={
                    <Icon iconName="downloadAttachment" fill="#818998" width={16} height={16} />
                }
                onClick={downloadAttachment}
                disabled={
                    !attachment?.originalUrl &&
                    !attachment?.previewUrl &&
                    !attachment?.previewSvgUrl
                }
            />
            {isImage && (
                <Image
                    src={attachment.thumbnailUrl ?? attachment.previewUrl ?? attachment.originalUrl}
                    width={28}
                    height={28}
                    draggable={false}
                    alt={attachment.name}
                    wrapperClassName={cls.MessageAttachmentImgWrapper}
                    className={cls.MessageAttachmentImg}
                />
            )}
            {attachment.previewSvgUrl && (
                <img src={attachment.previewSvgUrl} alt={attachment.name} width={28} height={28} />
            )}
            <UiText
                ref={elNameRef}
                variant="mediumPlain"
                title={isOverflowing ? attachment.name : ''}
                className={cls.MessageAttachmentName}
            >
                {attachment.name}
            </UiText>
        </Flex>
    );
};
